import { memo, useMemo, useState, useCallback, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sparkles,
  ChevronRight,
  Search,
  User,
  ShieldCheck,
  KeyRound,
  Languages,
  Check,
  LoaderCircle,
} from "lucide-react";
import {
  SunIcon,
  MoonIcon,
  LogoutIcon,
  MenuIcon,
} from "@/components/icons/AppIcons";
import { motion, AnimatePresence } from "framer-motion";
import { ease } from "@/lib/motion";
import { useAuthStore } from "@/stores/auth";
import { useThemeStore } from "@/stores/theme";
import { useUIStore } from "@/stores/ui";
import { useFontScaleStore } from "@/stores/fontScale";
import { useAiAssistantEnabled } from "@/features/settings/hooks/useSettings";
import Button from "@mui/material/Button";
import IconButton from "@/components/common/GoogleIconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import { BusinessUnitSelector } from "@/components/common/BusinessUnitSelector";
import { MobileFacilityContext } from "@/components/common/MobileFacilityContext";
import { AgentNotificationBell } from "@/features/agents/components/AgentNotificationBell";
import { UserMenuDialogs } from "@/components/account/UserMenuDialogs";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { SUPPORTED_LANGUAGES, type Language } from "@/lib/translation/language";
import { useLanguage } from "@/lib/translation/useLanguage";
import { changeLanguage } from "@/lib/translation/googleTranslate";
import { useGoogleText } from "@/lib/translation/useGoogleText";
import { usesGlobalFacilitySelection } from "@/lib/facilityScope";

const ROUTE_LABELS: Record<string, string> = {
  home: "Home",
  dashboard: "Dashboard",
  log: "Log",
  emissions: "Emissions",
  logemissions: "New Emission",
  bulkupload: "Template Upload",
  "bulk-upload": "Bulk Upload",
  upload: "Add Emissions",
  "upload-activity": "Upload Activity",
  customemission: "Custom Factor",
  agents: "Agent Activity",
  analytics: "Analytics",
  briefing: "ESG Briefing",
  "category-mapping": "Category Mapping",
  transferaccount: "Account Transfer",
  new: "New",
  create: "Create",
  edit: "Edit",
  targets: "Targets",
  assets: "Assets",
  offsets: "Offsets",
  organisation: "Organisation",
  facilities: "Facilities",
  employees: "Employees",
  disclosures: "Disclosures",
  datasource: "Data Sources",
  audits: "Audits",
  partners: "Partners",
  suppliers: "Suppliers",
  customers: "Customers",
  settings: "Settings",
  chat: "AI Assistant",
  reports: "Reports",
  resourcecentre: "Resource Centre",
  standardemissionfactor: "Standard Factors",
  customemissionfactor: "Custom Factors",
  organisationInformation: "Organisation Info",
  organisationpreference: "Preferences",
  security: "Security",
  exchangerates: "Exchange Rates",
  subscription: "Subscription",
};

// These prefixes group pages but do not have a page of their own.
const BREADCRUMB_GROUP_PATHS = new Set([
  "/organisation",
  "/partners",
  "/log",
  "/datasource",
  "/analytics",
  "/datasource/customemission",
  "/emissions",
]);

function getBreadcrumbs(
  pathname: string,
): { label: string; isLast: boolean; to?: string }[] {
  const segments = pathname.split("/").filter(Boolean);
  return segments.map((s, i) => {
    const path = `/${segments.slice(0, i + 1).join("/")}`;
    const isLast = i === segments.length - 1;
    return {
      label:
        ROUTE_LABELS[s] ||
        s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, " "),
      isLast,
      // Record IDs and grouping headings must not become links to missing routes.
      to:
        !isLast && ROUTE_LABELS[s] && !BREADCRUMB_GROUP_PATHS.has(path)
          ? path
          : undefined,
    };
  });
}

function HeaderFrame({
  children,
  showFacility,
}: {
  children: ReactNode;
  showFacility: boolean;
}) {
  return (
    <>
      <header className="@container flex h-[60px] shrink-0 items-center gap-0.5 sm:gap-3 border-b border-border/40 bg-card/90 px-2 sm:px-5">
        {children}
      </header>
      {showFacility && <MobileFacilityContext />}
    </>
  );
}

export const Header = memo(function Header({
  onOpenMobileSidebar,
  workspaceReady = true,
}: {
  onOpenMobileSidebar: () => void;
  workspaceReady?: boolean;
}) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const toggleAIPanel = useUIStore((s) => s.toggleAIPanel);
  const aiPanelOpen = useUIStore((s) => s.aiPanelOpen);
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const fontScale = useFontScaleStore((s) => s.fontScale);
  const setFontScale = useFontScaleStore((s) => s.setFontScale);
  const aiAssistantEnabled = useAiAssistantEnabled();
  const [profileOpen, setProfileOpen] = useState(false);
  const [appAuthSetupOpen, setAppAuthSetupOpen] = useState(false);
  const [appAuthEditOpen, setAppAuthEditOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [languageAnchor, setLanguageAnchor] = useState<HTMLElement | null>(
    null,
  );
  const languageState = useLanguage();
  const currentLanguage = languageState.language;
  const changingLanguage = languageState.status === "loading";
  const translateThemeLabel = useGoogleText([
    "Switch to dark mode",
    "Switch to light mode",
  ]);
  const location = useLocation();
  const navigate = useNavigate();
  const showFacilitySelector =
    workspaceReady && usesGlobalFacilitySelection(location.pathname);

  const breadcrumbs = useMemo(
    () => getBreadcrumbs(location.pathname),
    [location.pathname],
  );

  const displayName = user?.displayName;
  const initials = useMemo(() => {
    if (!displayName) return "??";
    return displayName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [displayName]);

  const handleThemeToggle = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const handleLanguageChange = useCallback((langCode: Language) => {
    setLanguageAnchor(null);
    void changeLanguage(langCode).catch((error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not change language. Please retry.",
      );
    });
  }, []);

  const fontScaleButtonSx = (active: boolean) => ({
    width: "1.75rem",
    height: "1.75rem",
    borderRadius: "0.5rem",
    ...(active
      ? {
          color: "var(--primary)",
          backgroundColor: "var(--primary-8)",
          "&:hover": {
            backgroundColor: "var(--primary-12)",
          },
        }
      : {
          color: "var(--muted-foreground)",
          "&:hover": {
            backgroundColor: "var(--foreground-4)",
            color: "var(--foreground)",
          },
        }),
  });

  const ThemeIcon = theme === "dark" ? SunIcon : MoonIcon;

  return (
    <HeaderFrame showFacility={showFacilitySelector}>
      {/* Mobile hamburger */}
      <IconButton
        onClick={onOpenMobileSidebar}
        className="lg:hidden"
        aria-label="Open navigation menu"
        sx={{
          padding: "0.5rem",
          borderRadius: "0.75rem",
          color: "var(--muted-foreground)",
          "&:hover": {
            backgroundColor: "var(--foreground-5)",
            color: "var(--foreground)",
          },
        }}
      >
        <MenuIcon className="h-[18px] w-[18px]" />
      </IconButton>

      {/* Breadcrumbs - hidden on mobile. min-w-0/shrink lets the trail
          truncate before it can push into the action cluster. */}
      <nav
        key={location.pathname}
        className="hidden min-w-0 shrink overflow-hidden items-center gap-1 text-[13px] sm:flex"
        aria-label="Breadcrumb"
      >
        {breadcrumbs.map((crumb, i) => (
          <span key={i} className="flex min-w-0 items-center gap-1">
            {i > 0 && (
              <ChevronRight
                size={11}
                className="text-muted-foreground/25 shrink-0 hidden sm:block"
                aria-hidden="true"
              />
            )}
            <span
              aria-current={crumb.isLast ? "page" : undefined}
              className={cn(
                "truncate transition-colors duration-200",
                crumb.isLast
                  ? "min-w-0 text-foreground font-medium"
                  : "max-w-[10rem] shrink-0 text-muted-foreground/50 hidden sm:inline",
              )}
            >
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className="block truncate rounded-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  {crumb.label}
                </Link>
              ) : (
                crumb.label
              )}
            </span>
          </span>
        ))}
      </nav>

      {/* The cluster never shrinks: container queries drop its lower-priority
          items instead of letting them overlap each other. */}
      <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
        {user?.role === 7 && location.pathname.includes("/log/emissions") && (
          <Button
            variant="outlined"
            size="small"
            className="hidden @min-[1280px]:inline-flex"
            onClick={() => navigate("/auditsummary")}
            sx={{ height: "2.25rem", textTransform: "none" }}
          >
            View Audit Summary
          </Button>
        )}
        {showFacilitySelector && <BusinessUnitSelector />}

        {/* Command palette trigger */}
        <Button
          variant="text"
          className="hidden @min-[700px]:flex"
          onClick={() => setCommandPaletteOpen(true)}
          aria-label="Open command palette"
          disabled={!workspaceReady}
          sx={{
            alignItems: "center",
            gap: "0.5rem",
            height: "2.25rem",
            border: "1px solid var(--border-40)",
            backgroundColor: "var(--muted-20)",
            color: "var(--muted-foreground)",
            "&:hover": {
              borderColor: "var(--border-60)",
              backgroundColor: "var(--foreground-4)",
              color: "var(--foreground)",
            },
          }}
        >
          <Search size={13} className="text-muted-foreground/60 shrink-0" />
          <span className="text-muted-foreground/50 hidden @min-[1000px]:inline">
            Search
          </span>
          <kbd className="ml-1 hidden @min-[1320px]:inline rounded-md border border-border/40 bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground/40">
            Ctrl+K
          </kbd>
        </Button>

        {/* Font size control */}
        <div
          className="hidden @min-[1180px]:flex items-center gap-0.5 rounded-lg border border-border/40 bg-muted/20 p-0.5"
          role="group"
          aria-label="Text size"
        >
          <IconButton
            onClick={() => setFontScale(1)}
            aria-label="Small text size"
            aria-pressed={fontScale === 1}
            sx={fontScaleButtonSx(fontScale === 1)}
          >
            <span
              translate="no"
              className="notranslate text-[11px] font-semibold leading-none"
            >
              A
            </span>
          </IconButton>
          <IconButton
            onClick={() => setFontScale(1.15)}
            aria-label="Large text size"
            aria-pressed={fontScale === 1.15}
            sx={fontScaleButtonSx(fontScale === 1.15)}
          >
            <span
              translate="no"
              className="notranslate text-[15px] font-semibold leading-none"
            >
              A
            </span>
          </IconButton>
        </div>

        {/* Language switcher */}
        <IconButton
          id="header-language-button"
          onClick={(e) => setLanguageAnchor(e.currentTarget)}
          aria-label="Change language"
          aria-haspopup="menu"
          aria-expanded={!!languageAnchor}
          aria-controls={languageAnchor ? "header-language-menu" : undefined}
          aria-busy={changingLanguage}
          title={languageState.error || "Change language"}
          sx={{
            width: "2.25rem",
            height: "2.25rem",
            borderRadius: "0.75rem",
            color: "var(--muted-foreground)",
            "&:hover": {
              backgroundColor: "var(--foreground-4)",
              color: "var(--foreground)",
            },
          }}
        >
          {changingLanguage ? (
            <LoaderCircle size={15} className="animate-spin" />
          ) : (
            <Languages size={15} />
          )}
        </IconButton>
        <span className="sr-only" role="status" aria-live="polite">
          {changingLanguage ? "Changing language…" : languageState.error || ""}
        </span>
        <Menu
          anchorEl={languageAnchor}
          open={!!languageAnchor}
          onClose={() => setLanguageAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            list: {
              id: "header-language-menu",
              "aria-labelledby": "header-language-button",
            },
          }}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <MenuItem
              key={lang.code}
              role="menuitemradio"
              aria-checked={currentLanguage === lang.code}
              selected={currentLanguage === lang.code}
              disabled={changingLanguage}
              onClick={() => handleLanguageChange(lang.code)}
              className="notranslate"
              lang={lang.code}
              sx={{ fontSize: "0.875rem" }}
            >
              {currentLanguage === lang.code && (
                <Check size={14} className="mr-2" />
              )}
              {lang.label}
            </MenuItem>
          ))}
          {languageState.status === "error" && (
            <MenuItem
              onClick={() =>
                handleLanguageChange(languageState.requestedLanguage)
              }
            >
              Retry translation
            </MenuItem>
          )}
        </Menu>

        {/* Theme toggle with animated icon swap */}
        <IconButton
          onClick={handleThemeToggle}
          translate="no"
          className="notranslate"
          aria-label={translateThemeLabel(
            theme === "light" ? "Switch to dark mode" : "Switch to light mode",
          )}
          sx={{
            width: "2.25rem",
            height: "2.25rem",
            borderRadius: "0.75rem",
            color: "var(--muted-foreground)",
            "&:hover": {
              backgroundColor: "var(--foreground-4)",
              color: "var(--foreground)",
            },
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={theme}
              initial={{ scale: 0.7, opacity: 0, rotate: -40 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 0.7, opacity: 0, rotate: 40 }}
              transition={ease}
            >
              <ThemeIcon className="h-[15px] w-[15px]" />
            </motion.div>
          </AnimatePresence>
        </IconButton>

        {/* AI panel toggle */}
        {workspaceReady && aiAssistantEnabled && (
          <IconButton
            onClick={toggleAIPanel}
            aria-label="Toggle AI Assistant panel"
            sx={{
              width: "2.25rem",
              height: "2.25rem",
              borderRadius: "0.75rem",
              ...(aiPanelOpen
                ? {
                    color: "var(--primary)",
                    backgroundColor: "var(--primary-8)",
                    boxShadow: "0 0 12px var(--primary-8)",
                    "&:hover": {
                      backgroundColor: "var(--primary-12)",
                    },
                  }
                : {
                    color: "var(--muted-foreground)",
                    "&:hover": {
                      backgroundColor: "var(--foreground-4)",
                      color: "var(--foreground)",
                    },
                  }),
            }}
          >
            <Sparkles size={14} />
          </IconButton>
        )}

        {workspaceReady && aiAssistantEnabled && <AgentNotificationBell />}

        <div className="hidden @min-[900px]:block w-px h-5 bg-border/40 mx-0.5" />

        {/* User menu */}
        <IconButton
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="User menu"
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          className="group"
          sx={{
            padding: "0.25rem",
            borderRadius: "9999px",
            "&:hover": {
              backgroundColor: "var(--accent-40)",
            },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--primary)",
              backgroundColor: "var(--primary-8)",
              outline: "2px solid var(--border-40)",
              outlineOffset: "2px",
              transition: "outline-color 300ms",
              ".MuiIconButton-root:hover &": {
                outlineColor: "var(--primary-20)",
              },
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
        <Menu
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { width: 224, marginTop: "0.5rem" } } }}
        >
          <div className="px-3 py-2.5">
            <p className="text-sm font-medium tracking-tight">
              {user?.displayName || "User"}
            </p>
            <p
              className="text-xs text-muted-foreground/60 mt-0.5 truncate"
              title={user?.email || ""}
            >
              {user?.email}
            </p>
          </div>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setProfileOpen(true);
            }}
            sx={{ gap: "0.625rem", borderRadius: "0.5rem" }}
          >
            <User className="h-[14px] w-[14px]" />
            Profile
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setAppAuthSetupOpen(true);
            }}
            sx={{ gap: "0.625rem", borderRadius: "0.5rem" }}
          >
            <ShieldCheck className="h-[14px] w-[14px]" />
            Setup 2FA Configuration
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setAppAuthEditOpen(true);
            }}
            sx={{ gap: "0.625rem", borderRadius: "0.5rem" }}
          >
            <ShieldCheck className="h-[14px] w-[14px]" />
            Edit 2FA Configuration
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setResetPasswordOpen(true);
            }}
            sx={{ gap: "0.625rem", borderRadius: "0.5rem" }}
          >
            <KeyRound className="h-[14px] w-[14px]" />
            Reset Password
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={async () => {
              if (!(await logout())) {
                toast.error("Couldn't sign out. Please try again.");
              }
            }}
            sx={{
              gap: "0.625rem",
              borderRadius: "0.5rem",
              color: "var(--destructive)",
              "& svg": { color: "inherit" },
              "&:hover": {
                backgroundColor: "var(--destructive)",
                color: "var(--destructive-foreground)",
              },
            }}
          >
            <LogoutIcon className="h-[14px] w-[14px]" />
            Sign out
          </MenuItem>
        </Menu>
      </div>

      <UserMenuDialogs
        profileOpen={profileOpen}
        onCloseProfile={() => setProfileOpen(false)}
        appAuthSetupOpen={appAuthSetupOpen}
        onCloseAppAuthSetup={() => setAppAuthSetupOpen(false)}
        appAuthEditOpen={appAuthEditOpen}
        onCloseAppAuthEdit={() => setAppAuthEditOpen(false)}
        resetPasswordOpen={resetPasswordOpen}
        onCloseResetPassword={() => setResetPasswordOpen(false)}
      />
    </HeaderFrame>
  );
});
